// File: src/components/admin/common/StyledTabs.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
//
// Reusable styled tabs component with consistent look & feel across the admin app.
// Supports icons, custom styling, and both scrollable and standard variants.

import React from 'react';
import { Tabs, Tab, Box } from '@mui/material';

// Standard tab styles used across the application
const tabStyles = {
    minHeight: 48,
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTab-root': {
        textTransform: 'none',
        minHeight: 48,
        fontWeight: 600,
        mr: 1,
        borderRadius: 2,
        transition: 'all 0.2s',
        color: 'text.secondary',
        '&:hover': {
            bgcolor: 'action.hover',
            color: 'text.primary',
        },
        '&.Mui-selected': {
            color: '#fff',
            bgcolor: 'primary.main',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
    },
};

/**
 * StyledTabs - A reusable tabs component with consistent styling
 *
 * @param {Object} props
 * @param {number} props.value - Current active tab index
 * @param {function} props.onChange - Tab change handler (event, newValue) => void
 * @param {Array} props.tabs - Array of tab configurations: { label: string, icon?: ReactNode, disabled?: boolean }
 * @param {boolean} props.scrollable - Whether to use scrollable variant (for many tabs)
 * @param {Object} props.sx - Additional sx styles to merge
 * @param {string} props.ariaLabel - Accessibility label for the tabs
 * @param {string} props.testId - Data-testid for testing
 */
const StyledTabs = ({
    value,
    onChange,
    tabs,
    scrollable = false,
    sx = {},
    ariaLabel = 'tabs',
    testId,
}) => {
    return (
        <Tabs
            value={value}
            onChange={onChange}
            aria-label={ariaLabel}
            variant={scrollable ? 'scrollable' : 'standard'}
            scrollButtons={scrollable ? 'auto' : false}
            allowScrollButtonsMobile={scrollable}
            sx={{ ...tabStyles, ...sx }}
            data-testid={testId}
        >
            {tabs.map((tab, index) => (
                <Tab
                    key={index}
                    icon={tab.icon}
                    iconPosition={tab.icon ? 'start' : undefined}
                    label={tab.label}
                    disabled={tab.disabled}
                    data-testid={tab.testId}
                />
            ))}
        </Tabs>
    );
};

/**
 * TabPanel - Content container for tab panels
 *
 * @param {Object} props
 * @param {ReactNode} props.children - Panel content
 * @param {number} props.value - Current active tab index
 * @param {number} props.index - This panel's index
 * @param {Object} props.sx - Additional sx styles
 */
export const TabPanel = ({ children, value, index, sx = {}, ...other }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3, ...sx }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

// Export the tab styles for cases where direct Tabs usage is needed
export const getTabStyles = () => tabStyles;

export default StyledTabs;
